import {
    ActionType,
    DataExportId,
    DataExportStatus,
    formatDateNoTime,
} from "@sp-crm/core";
import { useQuery } from "@tanstack/react-query";
import { Content, PageHeader, Stage } from "components/layout";
import { trpc } from "components/rpc/trpc";
import { Badge } from "components/ui/badge";
import { defaultLinkStyle } from "components/ui/link";
import { PrimaryButton } from "components/ui/primary-button";
import { Spinner } from "components/ui/spinner";
import React from "react";
import { Link, useParams } from "react-router-dom";
import { useIsAllowed } from "store/selectors/hooks";

const StatusBadge: React.FC<{ status: DataExportStatus }> = ({ status }) => {
    switch (status) {
        case DataExportStatus.Pending:
            return <Badge value="Pending" type="info" />;
        case DataExportStatus.Processing:
            return <Badge value="Processing" type="info" />;
        case DataExportStatus.Completed:
            return <Badge value="Completed" type="success" />;
        case DataExportStatus.Failed:
            return <Badge value="Failed" type="error" />;
        default: {
            const exhaustiveCheck: never = status;
            throw new Error(`Unhandled status: ${exhaustiveCheck}`);
        }
    }
};

export const ExportDetail: React.FC = () => {
    const csvDownloadEnabled = useIsAllowed(ActionType.ExportCsv);
    const { exportId } = useParams<{ exportId: string }>();
    const trpcClient = trpc.useTRPC();

    const query = useQuery({
        ...trpcClient.dataExport.getById.queryOptions({
            id: exportId as DataExportId,
        }),
    });

    const handleDownload = () => {
        if (query.data?.fileId) {
            window.location.href = `/api/files/download/${query.data.fileId}`;
        }
    };

    if (!csvDownloadEnabled) {
        return null;
    }

    if (query.isLoading) {
        return (
            <div className="flex justify-center py-8">
                <Spinner />
            </div>
        );
    }

    if (query.isError) {
        return (
            <div className="space-y-4">
                <div className="text-red-600">
                    Error loading export: {query.error?.message}
                </div>
                <BackToExportsLink />
            </div>
        );
    }

    if (!query.data) {
        return (
            <div className="space-y-4">
                <div className="text-gray-600">Export not found.</div>
                <BackToExportsLink />
            </div>
        );
    }

    const dataExport = query.data;

    return (
        <Stage>
            <PageHeader title="Data Export" />

            <div className="mb-4 px-4">
                <BackToExportsLink />
            </div>
            <Content>
                <div className="space-y-6">
                    <div className="flex items-center justify-between mb-4">
                        <StatusBadge status={dataExport.status} />
                    </div>

                    <div className="space-y-2 text-sm text-gray-600 mb-6">
                        <div>Created: {formatDateNoTime(dataExport.createdAt)}</div>
                        {dataExport.completedAt && (
                            <div>
                                Completed: {formatDateNoTime(dataExport.completedAt)}
                            </div>
                        )}
                        {dataExport.requestedByName && (
                            <div>Requested by: {dataExport.requestedByName}</div>
                        )}
                    </div>

                    {dataExport.status === DataExportStatus.Completed &&
                        dataExport.fileId && (
                            <div className="space-y-2">
                                <p className="text-green-700 font-medium">
                                    Your export is ready.
                                </p>
                                <PrimaryButton onClick={handleDownload}>
                                    Download
                                </PrimaryButton>
                            </div>
                        )}

                    {dataExport.status === DataExportStatus.Pending && (
                        <p className="text-gray-600">
                            Your export is pending. Please check back later.
                        </p>
                    )}

                    {dataExport.status === DataExportStatus.Processing && (
                        <p className="text-gray-600">
                            Your export is being processed. Please check back later.
                        </p>
                    )}

                    {dataExport.status === DataExportStatus.Failed && (
                        <div className="space-y-2">
                            <p className="text-red-600 font-medium">Export failed.</p>
                            {dataExport.errorMessage && (
                                <p className="text-sm text-gray-600">
                                    {dataExport.errorMessage}
                                </p>
                            )}
                        </div>
                    )}
                </div>
            </Content>
        </Stage>
    );
};

const BackToExportsLink: React.FC = () => (
    <Link to="/exports" className={defaultLinkStyle}>
        &larr; Back to exports
    </Link>
);
